import './shopSwitch.scss';
import { ComponentRegistry } from '@ts/utils/ComponentRegistry';
import { Backdrop } from "@ts/utils";

type ShopSwitchHook = (trigger: HTMLButtonElement | HTMLDivElement | HTMLAnchorElement) => void | Promise<void>;
type ShopSwitchFailHook = (error: unknown, trigger: HTMLButtonElement | HTMLDivElement | HTMLAnchorElement) => void | Promise<void>;
type ShopSwitchOptions = {
    onClick?: ShopSwitchHook;
    onOpen?: ShopSwitchHook;
    onClose?: ShopSwitchHook;
    onSuccess?: ShopSwitchHook;
    onFail?: ShopSwitchFailHook;
    checkCartContentsUrl?: string;
    clearCartContentsUrl?: string;
    requestVerificationToken?: string;
    openOnHover?: boolean;
    hoverMediaQuery?: string;
    closeDelay?: number;
};

const DEFAULT_HOVER_MEDIA_QUERY = '(min-width: 992px) and (hover: hover) and (pointer: fine)';
const DEFAULT_CLOSE_DELAY = 150;

type CartContentsResult = { hasItems: boolean };
type ClearCartContentsResult = { success: boolean };

export class ShopSwitch {

    private readonly id: string;
    private static registry = new ComponentRegistry<ShopSwitch>('shop-switch');
    private readonly trigger: HTMLButtonElement| HTMLDivElement | HTMLAnchorElement;
    private newShopUrl: string;
    private backdrop: Backdrop | null = null;
    private hoverQuery: MediaQueryList | null = null;
    private hoverTargets: HTMLElement[] = [];
    private closeTimer: number | undefined;

    private readonly onPointerEnter = () => {
        if (!this.isHoverMode()) return;
        this.cancelScheduledClose();
        void this.openDropdown('hover');
    };

    private readonly onPointerLeave = () => {
        if (!this.isHoverMode() || !this.isOpen()) return;
        this.cancelScheduledClose();
        this.closeTimer = window.setTimeout(
            () => void this.closeDropdown(),
            this.options.closeDelay ?? DEFAULT_CLOSE_DELAY
        );
    };

    private readonly onHoverQueryChange = () => {
        if (this.isOpen()) void this.closeDropdown();
    };

   private constructor(
        trigger: HTMLButtonElement | HTMLDivElement | HTMLAnchorElement,
        private options: ShopSwitchOptions
    ) {
        this.id = ShopSwitch.registry.register(trigger, this);
        this.trigger = trigger;
        this.setClickEvent();
        this.setHoverEvents();
    }

    private getDropdown(): HTMLElement | null {
        const controlsId = this.trigger.getAttribute('aria-controls');
        return controlsId ? document.getElementById(controlsId) : null;
    }

    private isOpen(): boolean {
        return this.trigger.getAttribute('aria-expanded') === 'true';
    }

    private isHoverMode(): boolean {
        return this.hoverQuery?.matches === true;
    }

    private cancelScheduledClose(): void {
        if (this.closeTimer === undefined) return;
        window.clearTimeout(this.closeTimer);
        this.closeTimer = undefined;
    }

    private setHoverEvents() {
        if (!this.options.openOnHover) return;

        this.hoverQuery = window.matchMedia(this.options.hoverMediaQuery ?? DEFAULT_HOVER_MEDIA_QUERY);
        this.hoverQuery.addEventListener('change', this.onHoverQueryChange);

        // The dropdown is a sibling of the trigger, so both must keep it open.
        this.hoverTargets = [this.trigger, this.getDropdown()].filter((element): element is HTMLElement => element !== null);
        for (const element of this.hoverTargets) {
            element.addEventListener('mouseenter', this.onPointerEnter);
            element.addEventListener('mouseleave', this.onPointerLeave);
        }
    }

    private setClickEvent() {
        this.trigger.addEventListener('click', async (e) => {
            e.preventDefault();

            // In hover mode mouse clicks would close what hover just opened; keyboard activation reports detail 0.
            if (this.isHoverMode() && (e as MouseEvent).detail > 0) return;

            if (typeof this.options.onClick === 'function') {
                await this.options.onClick(this.trigger);
            }

            if (this.isOpen()) {
                await this.closeDropdown();
            } else {
                await this.openDropdown('click');
            }
        });
    }

    private async openDropdown(source: 'click' | 'hover'): Promise<void> {
        const dropdown = this.getDropdown();
        if (!dropdown || this.isOpen()) return;

        this.trigger.dataset.openSource = source;
        this.trigger.setAttribute('aria-expanded', 'true');
        dropdown.hidden = false;
        this.backdrop = new Backdrop(() => this.closeDropdown());
        this.backdrop.show();
        if (typeof this.options.onOpen === 'function') {
            await this.options.onOpen(this.trigger);
        }
    }

    private async closeDropdown(): Promise<void> {
        this.cancelScheduledClose();
        const dropdown = this.getDropdown();
        if (!dropdown) return;

        delete this.trigger.dataset.openSource;
        this.trigger.setAttribute('aria-expanded', 'false');
        dropdown.hidden = true;
        this.backdrop?.hide();
        this.backdrop = null;
        if (typeof this.options.onClose === 'function') {
            await this.options.onClose(this.trigger);
        }
    }

    public static create(
        target: string | HTMLElement | NodeListOf<HTMLElement> | HTMLElement[],
        options: ShopSwitchOptions = {}
    ): ShopSwitch[] | null {
        const triggers = ComponentRegistry.processTargets<ShopSwitch, HTMLElement>(
            ShopSwitch.registry,
            target,
            [HTMLDivElement, HTMLButtonElement, HTMLAnchorElement]
        ) as (HTMLDivElement | HTMLButtonElement | HTMLAnchorElement)[];

        if (!triggers.length) return null;
        
        return triggers.map(trigger => new ShopSwitch(trigger, options));
    }

    public static getShopSwitch(id: string): ShopSwitch | null {
        return ShopSwitch.registry.get(id) ?? null;
    }

    public close(): void {
        void this.closeDropdown();
    }

    public destroy(): void {
        this.cancelScheduledClose();
        this.hoverQuery?.removeEventListener('change', this.onHoverQueryChange);
        for (const element of this.hoverTargets) {
            element.removeEventListener('mouseenter', this.onPointerEnter);
            element.removeEventListener('mouseleave', this.onPointerLeave);
        }
        ShopSwitch.registry.unregister(this.id);
    }
}